/**
 * Registrazione del service worker.
 *
 * `registerType: 'prompt'` (vite.config.ts): l'aggiornamento non avviene mai a
 * sorpresa mentre l'utente sta usando l'app in viaggio. Quando c'e' una versione
 * nuova, l'app lo segnala e l'utente decide.
 *
 * Perche' `applicaAggiornamento` esiste: senza, il segnale non porta da nessuna
 * parte. Un'app installata continuerebbe a servire per sempre la versione in
 * cache, e nessuna correzione arriverebbe mai a chi l'ha gia' sul telefono —
 * cioe' proprio a chi la usa davvero. E' successo: la prima versione pubblicata
 * aveva una schermata rotta, e chi l'aveva installata restava bloccato li'.
 */

let updateReady = false;
let workerInAttesa: ServiceWorker | null = null;
const listeners = new Set<() => void>();

function segnalaAggiornamento(worker: ServiceWorker): void {
  updateReady = true;
  workerInAttesa = worker;
  for (const listener of listeners) listener();
}

export function onUpdateAvailable(listener: () => void): () => void {
  listeners.add(listener);
  if (updateReady) listener();
  return () => listeners.delete(listener);
}

export function isUpdateReady(): boolean {
  return updateReady;
}

/**
 * Applica la versione nuova.
 *
 * Il worker in attesa non prende il controllo finche' non glielo si chiede: gli
 * si manda `SKIP_WAITING` e si ricarica quando il cambio e' avvenuto. Ricaricare
 * subito non basterebbe — la pagina tornerebbe servita dal worker vecchio.
 */
export function applicaAggiornamento(): void {
  if (workerInAttesa === null) {
    window.location.reload();
    return;
  }

  // `controllerchange` dice che il worker nuovo ha preso il posto del vecchio:
  // solo allora ricaricare serve a qualcosa.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    window.location.reload();
  });

  workerInAttesa.postMessage({ type: 'SKIP_WAITING' });
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  if (import.meta.env.DEV) return;

  window.addEventListener('load', () => {
    void navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        // Un aggiornamento puo' essere gia' pronto da una visita precedente:
        // senza questo controllo resterebbe in attesa all'infinito.
        if (registration.waiting !== null && navigator.serviceWorker.controller !== null) {
          segnalaAggiornamento(registration.waiting);
        }

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (installing === null) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller !== null) {
              segnalaAggiornamento(installing);
            }
          });
        });

        // Controllo periodico: un'app installata puo' restare aperta per giorni
        // senza mai ricaricarsi, e altrimenti non si accorgerebbe di nulla.
        setInterval(
          () => {
            void registration.update();
          },
          60 * 60 * 1000,
        );
      })
      .catch((error: unknown) => {
        // Il fallimento non e' silenzioso: l'app funziona comunque online, ma il
        // problema resta visibile in console invece di sparire.
        console.warn('[pwa] registrazione del service worker fallita:', error);
      });
  });
}
